import type { Character, StatAdvancement } from '../domain/character.ts';
import type { StatBlock } from '../domain/stats.ts';
import { STAT_IDS } from '../domain/stats.ts';
import { applyChanges } from '../rules/characterChanges.ts';
import { batch } from './characterModificationService.ts';

/** Core pp.128,169: Level 1 grants no points; tutorial gains bank until Floor 3. */
export function statAdvancementFor(character: Character): StatAdvancement {
  const stored = character.statAdvancement ?? {
    levelAccountedFor: character.level,
    unspentPoints: character.floor < 3 ? (character.level - 1) * 3 : 0,
    needsReview: character.floor >= 3,
  };
  return {
    ...stored,
    unspentPoints: stored.unspentPoints + Math.max(0, character.level - stored.levelAccountedFor) * 3,
  };
}

export function allocateCrawlerStatPoints(
  character: Character,
  allocation: StatBlock,
  inSaferoom: boolean,
  inCombat: boolean,
  now: string,
): { ok: true; character: Character } | { ok: false; reason: string } {
  if (character.floor < 3) return { ok: false, reason: 'Stat Points become available on Floor 3 and deeper.' };
  if (!inSaferoom || inCombat) return { ok: false, reason: 'Reach a saferoom outside combat before allocating Stat Points.' };
  const points = statAdvancementFor(character);
  if (points.needsReview) return { ok: false, reason: 'The GM must review this existing crawler’s unspent Stat Points first.' };
  if (STAT_IDS.some((id) => !Number.isSafeInteger(allocation[id]) || allocation[id] < 0)) return { ok: false, reason: 'Allocate whole, nonnegative Stat Points.' };
  const total = STAT_IDS.reduce((sum, id) => sum + allocation[id], 0);
  if (total < 1 || total > points.unspentPoints) return { ok: false, reason: `Allocate between 1 and ${points.unspentPoints} available Stat Points.` };
  const stats = { ...character.unenhancedStats };
  for (const id of STAT_IDS) stats[id] += allocation[id];
  const out = applyChanges(character, batch('advancement', 'Stat Points allocated in a saferoom', [
    { kind: 'set-stats-and-advancement', stats, advancement: { levelAccountedFor: character.level, unspentPoints: points.unspentPoints - total, needsReview: false } },
  ]), { now });
  return out.ok ? { ok: true, character: out.character } : { ok: false, reason: out.problems.map((p) => p.message).join('; ') };
}
